import type { ReactNode } from "react";
import type { ClientSummary, DraftCounts } from "@patent-studio/api-types";
import { Panel } from "@/components/ui/Panel";
import type { Tone } from "@/components/ui/tone";
import { cn } from "@/lib/utils";
import { MaybeLink } from "../layout/MaybeLink";
import { GoesTo } from "../layout/wayfinding";
import { ClientLogo, UnassignedMark } from "./ClientLogo";
import { UNASSIGNED_PATH, UNASSIGNED_TITLE, clientHubPath } from "./paths";

/**
 * A row of a client page's list that lifts under the pointer.
 *
 * The item stays where it is and the panel inside it lifts. A panel that took
 * the hover itself would move up out from under a pointer resting on its lower
 * edge, lose the hover, drop back under the pointer and lift again, and its
 * transition would draw that loop as a flicker.
 *
 * The lift is a transform, which gives the panel a stacking context of its own,
 * so a menu opened from the card is layered only among the card's contents and
 * the next item is drawn over it. The item is raised while it is hovered or
 * holds the focus, and higher still while anything on it stands open, since a
 * popover does not always take the focus with it and a neighbour hovered below
 * an open menu would otherwise be drawn over it. At rest it is a layer of
 * its own too, so what it raises inside itself, its Options, stays under a
 * raised neighbour.
 */
export function LiftingItem({
  testId,
  tone,
  className,
  children,
}: {
  testId: string;
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <li
      data-testid={testId}
      className="group relative z-0 hover:z-10 focus-within:z-10 has-[[aria-expanded=true]]:z-20"
    >
      <Panel
        pad="none"
        tone={tone}
        className={cn(
          "relative flex items-center gap-5 px-[18px] py-[15px] transition-[box-shadow,transform] duration-200 focus-within:ring-2 focus-within:ring-ring/50 group-hover:-translate-y-0.5 group-hover:shadow-[0_8px_20px_rgba(15,0,27,0.09)]",
          className,
        )}
      >
        {children}
      </Panel>
    </li>
  );
}

/**
 * One card of the landing list: the picture, the words, the counts and the way in.
 *
 * The whole card opens the hub, through a link on the name stretched over the
 * card, so the name is what a screen reader hears as the link and anything
 * else on the card that acts, such as the key contact, stays a control of its
 * own above it.
 */
function CardShell({
  to,
  name,
  logo,
  meta,
  counts,
  corner,
  testId,
}: {
  to: string;
  name: string;
  logo: ReactNode;
  meta: ReactNode;
  counts: DraftCounts;
  /** Acts on the card that are not opening it, drawn above the stretched link. */
  corner?: ReactNode;
  testId: string;
}) {
  return (
    <LiftingItem
      testId={testId}
      className="shadow-[0_1px_2px_rgba(15,0,27,0.05)]"
    >
      {logo}
      <div className="min-w-0 flex-1">
        <h2 className="text-[17.5px] leading-snug font-bold tracking-[-0.01em]">
          <MaybeLink
            to={to}
            className="after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none"
          >
            {name}
          </MaybeLink>
        </h2>
        <div className="mt-1.5 flex flex-col gap-[3px] pl-2.5 text-[12px] text-muted-foreground">
          {meta}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-[12.5px] text-muted-foreground">
        <span>{counts.in_progress} in progress</span>
        <span>{counts.completed} completed</span>
      </div>
      {corner !== undefined && <div className="relative z-10">{corner}</div>}
      <GoesTo />
    </LiftingItem>
  );
}

export function ClientCard({
  client,
  onLogoBroken,
  contact,
  corner,
}: {
  client: ClientSummary;
  onLogoBroken: () => void;
  /** The key contact, as a control of its own above the stretched link. */
  contact: ReactNode;
  corner?: ReactNode;
}) {
  return (
    <CardShell
      testId={`client-card-${client.client_id}`}
      to={clientHubPath(client.client_id)}
      name={client.name}
      logo={
        <ClientLogo
          name={client.name}
          logoUrl={client.logo_url}
          onBroken={onLogoBroken}
        />
      }
      meta={
        <>
          <span className="italic">{client.industry}</span>
          <span>Key contact: {contact}</span>
          <span>Client ref {client.reference}</span>
        </>
      }
      counts={{
        in_progress: client.drafts_in_progress,
        completed: client.drafts_completed,
      }}
      corner={corner}
    />
  );
}

/** The card at the foot of the list for the caller's drafts made before clients existed. */
export function UnassignedCard({ counts }: { counts: DraftCounts }) {
  return (
    <CardShell
      testId="unassigned-card"
      to={UNASSIGNED_PATH}
      name={UNASSIGNED_TITLE}
      logo={<UnassignedMark />}
      meta={
        <span className="italic">
          Drafts made before they could be filed under a client.
        </span>
      }
      counts={counts}
    />
  );
}
